import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { useConvexAuth } from 'convex/react'
import { useEffect, useRef } from 'react'
import { api } from '../../../convex/_generated/api'

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Hook to get the current user's active profile and group.
 * Returns TanStack Query result with { data, isPending, error }.
 *
 * Automatically skips the query when not authenticated.
 */
export function useCurrentProfile() {
  const { isLoading, isAuthenticated } = useConvexAuth()

  return useQuery({
    ...convexQuery(api.users.getCurrentProfile, {}),
    enabled: !isLoading && isAuthenticated,
  })
}

/**
 * Current profile with Suspense (for profile route).
 * Preload via loader: getCurrentProfile.
 */
export function useCurrentProfileSuspense() {
  const { data } = useSuspenseQuery(
    convexQuery(api.users.getCurrentProfile, {})
  )
  return { data }
}

/**
 * Hook to get all profiles for the current user (main + sub-profiles).
 */
export function useMyProfiles() {
  const { isLoading, isAuthenticated } = useConvexAuth()

  return useQuery({
    ...convexQuery(api.users.getMyProfiles, {}),
    enabled: !isLoading && isAuthenticated,
  })
}

/**
 * Hook to get all members in the current user's group.
 */
export function useGroupMembers() {
  const { isLoading, isAuthenticated } = useConvexAuth()

  return useQuery({
    ...convexQuery(api.users.getGroupMembers, {}),
    enabled: !isLoading && isAuthenticated,
  })
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Hook to ensure the current authenticated user has a profile.
 * 
 * This should be called after authentication to create the profile/group
 * if they don't exist yet.
 */
export function useEnsureProfile() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.ensureUserExists),
  })
}

/**
 * Hook to switch the active profile.
 */
export function useSwitchProfile() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.switchProfile),
  })
}

/**
 * Hook to create a sub-profile (dependent).
 */
export function useCreateSubProfile() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.createSubProfile),
  })
}

/**
 * Hook to update the current profile.
 */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.updateProfile),
  })
}

/**
 * Hook to update group settings (admin only).
 */
export function useUpdateGroup() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.updateGroup),
  })
}

/**
 * Hook to remove a member from the household (admin only).
 */
export function useRemoveMember() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.removeMemberFromGroup),
  })
}

/**
 * Hook to leave the current household (non-owner only).
 */
export function useLeaveHousehold() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.leaveHousehold),
  })
}

/**
 * Hook to set a PIN for the current profile.
 */
export function useSetPin() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.setPin),
  })
}

/**
 * Hook to change the PIN for the current profile.
 */
export function useChangePin() {
  return useMutation({
    mutationFn: useConvexMutation(api.users.changePin),
  })
}

// =============================================================================
// COMPOSITE HOOKS
// =============================================================================

/**
 * Hook to automatically ensure profile exists for authenticated users.
 *
 * Strategy (Convex + Clerk best practice):
 * - Ensure-on-auth: Call ensureUserExists as soon as auth is ready, not when
 *   getCurrentProfile returns null. That avoids races and ambiguous "null = loading vs missing".
 * - Server owns create logic: ensureUserExists is idempotent; we only trigger it from the client.
 * - Single source of truth: getCurrentProfile (reactive) drives isReady/profile; we don't
 *   depend on its value to decide when to ensure.
 *
 * Uses useConvexAuth (not Clerk's useAuth) so the Convex JWT is synced before we run.
 *
 * Usage:
 * ```tsx
 * const { isReady, profile, isPending } = useProfileSetup()
 * if (!isReady) return <LoadingSpinner />
 * // profile is guaranteed to exist here
 * ```
 */
export function useProfileSetup() {
  const { isLoading: isAuthLoading, isAuthenticated } = useConvexAuth()
  const hasEnsured = useRef(false)

  const { data: profileData, isPending: isQueryPending } = useCurrentProfile()
  const ensureProfile = useEnsureProfile()

  // Ensure profile as soon as we're authenticated (idempotent). One effect, minimal deps.
  // - No dependency on profileData: we don't wait for "null" to avoid races and extra effect runs.
  // - Reset hasEnsured on sign-out so a new sign-in runs ensure again.
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      if (!hasEnsured.current) {
        hasEnsured.current = true
        ensureProfile.mutate({})
      }
    } else if (!isAuthenticated && !isAuthLoading) {
      hasEnsured.current = false
    }
  }, [isAuthLoading, isAuthenticated, ensureProfile.mutate])

  // User is ready when:
  // - Auth is loaded AND
  // - Either not authenticated OR (authenticated AND profile exists)
  const isLoaded = !isAuthLoading
  const isReady = isLoaded && profileData !== undefined && profileData !== null
  const isPending = isAuthLoading || isQueryPending
  
  return {
    isReady,
    isPending,
    // Keep isLoading as alias for backwards compatibility
    isLoading: isPending,
    profile: profileData?.profile ?? null,
    group: profileData?.group ?? null,
    isMain: profileData?.isMain ?? false,
    groupHasAssets: profileData?.groupHasAssets ?? false,
    error: ensureProfile.error,
  }
}

/**
 * Alias for useProfileSetup for backwards compatibility.
 * Returns the same data as useProfileSetup.
 */
export function useProfile() {
  return useProfileSetup()
}

